import { useEffect, useRef, useState } from 'react'
import { Modal, Button, Text, Stack, Alert, Group, Paper } from '@mantine/core'
import { IconScan, IconAlertCircle, IconX } from '@tabler/icons-react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'

interface BarcodeScannerProps {
  opened: boolean
  onClose: () => void
  onScan: (value: string) => void
}

export function BarcodeScanner({ opened, onClose, onScan }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const scannerRef = useRef<any>(null)
  const decodedRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)

  const stopScanner = () => {
    try {
      if (scannerRef.current) {
        BrowserMultiFormatReader.releaseAllStreams()
        scannerRef.current = null
      }
      if (videoRef.current) videoRef.current.srcObject = null
    } catch (_) { /* ignore */ }
    setScanning(false)
  }

  const startScanner = async () => {
    setError(null)
    decodedRef.current = false
    const videoElement = videoRef.current
    if (!videoElement) {
      setError('ไม่พบองค์ประกอบกล้อง กรุณาลองใหม่')
      return
    }
    try {
      const codeReader = new BrowserMultiFormatReader()
      scannerRef.current = codeReader
      setScanning(true)
      await codeReader.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoElement,
        (result, err) => {
          if (result && !decodedRef.current) {
            decodedRef.current = true
            const text = result.getText().trim()
            stopScanner()
            onScan(text)
            onClose()
          }
          if (err && !(err instanceof NotFoundException)) {
            // ignore continuous no-result errors
          }
        }
      )
    } catch (err: any) {
      setScanning(false)
      if (err?.name === 'NotAllowedError') {
        setError('กรุณาอนุญาตการเข้าถึงกล้อง')
      } else if (err?.name === 'NotFoundError') {
        setError('ไม่พบกล้องในอุปกรณ์นี้')
      } else {
        setError(`ไม่สามารถเริ่มกล้องได้: ${err?.message ?? err}`)
      }
    }
  }

  const handleClose = () => {
    stopScanner()
    onClose()
  }

  useEffect(() => {
    if (opened) {
      startScanner()
    } else {
      stopScanner()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopScanner() }
  }, [])

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap={8}>
          <IconScan size={18} />
          <Text fw={600}>สแกน Barcode / QR Code</Text>
        </Group>
      }
      size="sm"
      centered
    >
      <Stack gap="sm">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
            <Button size="xs" mt="xs" onClick={startScanner}>ลองใหม่</Button>
          </Alert>
        )}

        <Paper withBorder radius="md" style={{ overflow: 'hidden', background: '#000', minHeight: 280 }}>
          <video
            ref={videoRef}
            style={{ width: '100%', height: 280, display: 'block', objectFit: 'cover' }}
            autoPlay muted playsInline
          />
        </Paper>

        {scanning && (
          <Text size="xs" c="dimmed" ta="center">
            จ่อกล้องไปที่ Barcode หรือ QR Code
          </Text>
        )}

        <Button
          variant="default"
          leftSection={<IconX size={16} />}
          onClick={handleClose}
          fullWidth
        >
          ยกเลิก
        </Button>
      </Stack>
    </Modal>
  )
}
